import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface DreCategoryDetail {
  category_id: string;
  category_name: string;
  category_color: string;
  category_icon: string;
  amount: number;
}

export interface DreSection {
  section: string;
  label: string;
  type: "receita" | "despesa";
  sort_order: number;
  total: number;
  categories: DreCategoryDetail[];
}

export interface DreTotals {
  receita_bruta: number;
  custos: number;
  lucro_bruto: number;
  despesas_operacionais: number;
  resultado_operacional: number;
  despesas_financeiras: number;
  receitas_financeiras: number;
  resultado_liquido: number;
}

export interface DreMonthlyBreakdown {
  month: number;
  month_label: string;
  receitas: number;
  despesas: number;
  resultado: number;
}

export interface DreComparison {
  receita_change_percent: number;
  despesa_change_percent: number;
  resultado_change_percent: number;
}

export interface DreReport {
  sections: DreSection[];
  totals: DreTotals;
  monthly_breakdown: DreMonthlyBreakdown[];
  comparison: DreComparison;
  isLoading: boolean;
  error: Error | null;
}

export function useDre(
  groupId: string | null,
  year: number,
  month: number | null,
  quarter: number | null
): DreReport {
  const { data: rpcResult, isLoading, error } = useQuery({
    queryKey: ["dre", groupId, year, month, quarter],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase.rpc("get_dre_report", {
        p_group_id: groupId,
        p_year: year,
        p_month: month,
        p_quarter: quarter,
      } as any);

      if (error) throw error;
      return data as any;
    },
  });

  const sections: DreSection[] = (rpcResult?.sections || []).map((s: any) => ({
    ...s,
    total: Number(s.total),
    categories: (s.categories || []).map((c: any) => ({
      ...c,
      amount: Number(c.amount),
    })),
  }));

  const rawTotals = rpcResult?.totals || {};
  const totals: DreTotals = {
    receita_bruta: Number(rawTotals.receita_bruta || 0),
    custos: Number(rawTotals.custos || 0),
    lucro_bruto: Number(rawTotals.lucro_bruto || 0),
    despesas_operacionais: Number(rawTotals.despesas_operacionais || 0),
    resultado_operacional: Number(rawTotals.resultado_operacional || 0),
    despesas_financeiras: Number(rawTotals.despesas_financeiras || 0),
    receitas_financeiras: Number(rawTotals.receitas_financeiras || 0),
    resultado_liquido: Number(rawTotals.resultado_liquido || 0),
  };

  const monthly_breakdown: DreMonthlyBreakdown[] = (rpcResult?.monthly_breakdown || []).map((m: any) => ({
    month: Number(m.month),
    month_label: m.month_label,
    receitas: Number(m.receitas || 0),
    despesas: Number(m.despesas || 0),
    resultado: Number(m.resultado || 0),
  }));

  const rawComparison = rpcResult?.comparison || {};
  const comparison: DreComparison = {
    receita_change_percent: Number(rawComparison.receita_change_percent || 0),
    despesa_change_percent: Number(rawComparison.despesa_change_percent || 0),
    resultado_change_percent: Number(rawComparison.resultado_change_percent || 0),
  };

  return {
    sections,
    totals,
    monthly_breakdown,
    comparison,
    isLoading,
    error: error as Error | null,
  };
}
